import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import { useFormikContext } from 'formik'

import { EMPTY_OBJECT } from '../../../../constants/common'
import {
  QA_NAME_CANCEL_BTN,
  QA_NAME_END_BTN,
  QA_NAME_SUBMIT_BTN,
} from '../../../../constants/qa'
import { SIZE } from '../../../../constants/size'
import { useBeforeUnload, useBlockNavigation } from '../../../../hooks'
import { preventDefault } from '../../../../utils/events'
import { AIGretaLoader } from '../../../AIGreta'
import { useFileFormFieldsStatus } from '../../../File'
import {
  FormBody,
  FormButtonCancel,
  FormFieldsContainer,
  FormInlineNotifications,
  useFormValidationErrorsNotification,
} from '../../../Form'
import { Content } from '../../../Modal'
import Text from '../../../Text'
import { useRequestWizardDraft } from '../../Request.hooks'
import { CARD_STAGE } from '../RequestWizardForm.constants'
import {
  getCardKey,
  getCardStageDividerPosition,
  getWizardChunkSignature,
  isBotVisible,
  isWorkflowEnd,
} from '../RequestWizardForm.utils'

import { BotCard } from './BotCard'
import { Card } from './Card'

import * as styles from './RequestWizardCardStageFormFields.module.css'

function RequestWizardCardStageFormFields({
  ContentComponent = Content,
  chunk,
  contentComponentProps = {
    size: SIZE.X_LARGE,
  },
  filesOptions = EMPTY_OBJECT,
  isAILoading,
  isCardLoading,
  notifications,
  uuid,
  onCancel,
  onFinish,
  onRemoveNotification,
  onSetNotification,
}) {
  const { t } = useTranslation()

  const { filesError, filesUploading, setFileError, setFileUploading } =
    useFileFormFieldsStatus()

  useFormValidationErrorsNotification({
    files: { filesError, filesUploading },
    onRemoveNotification,
    onSetNotification,
  })

  const { dirty, values } = useFormikContext()

  const { setDraft: setWizardDraft } = useRequestWizardDraft()

  const chunkSignature = getWizardChunkSignature(chunk)

  useEffect(() => {
    if (dirty) {
      setWizardDraft({
        chunkSignature,
        stage: CARD_STAGE,
        uuid,
        values,
      })
    }
  }, [chunkSignature, dirty, uuid, values])

  useBeforeUnload(true, true) // show browser confirmation on unload in any cases on card stage

  useBlockNavigation(true)

  function handleCancel() {
    onCancel({
      dirty: true,
    })
  }

  function handleCloseOnWorkFlowFinished(event) {
    preventDefault(event)
    onFinish?.()
  }

  const isWorkFlowFinished = isWorkflowEnd(chunk)

  return (
    <FormBody
      actionsProps={{
        ButtonAdd: false,
        ButtonCancel: !isWorkFlowFinished ? FormButtonCancel : null,
        ButtonClear: false,
        UploadComponent: false,
        cancelProps: {
          qaName: QA_NAME_CANCEL_BTN,
          title: 'Modal.WizardCancelBtnKey',
          onClick: handleCancel,
        },
        className: styles.actions,
        submitProps: {
          disabled: isAILoading,
          isLoading: isCardLoading && !isAILoading,
          qaName: isWorkFlowFinished ? QA_NAME_END_BTN : QA_NAME_SUBMIT_BTN,
          title: isWorkFlowFinished
            ? 'Modal.WizardEndBtnKey'
            : 'Modal.WizardContinueBtnKey',
          onClick: isWorkFlowFinished
            ? handleCloseOnWorkFlowFinished
            : undefined,
        },
      }}
    >
      <ContentComponent {...contentComponentProps} className={styles.content}>
        <FormInlineNotifications notifications={notifications} />

        <div className={styles.aiLoaderWrap}>
          {isAILoading ? <AIGretaLoader /> : null}

          <FormFieldsContainer className={styles.formFieldsContainer}>
            {isBotVisible(chunk) ? <BotCard /> : null}
            {chunk ? (
              <div
                className={classNames({ [styles.card]: isBotVisible(chunk) })}
              >
                {chunk.map((card, index) => (
                  <Card
                    card={card}
                    filesOptions={{
                      ...filesOptions,
                      setFileError,
                      setFileUploading,
                    }}
                    hasDivider={getCardStageDividerPosition(chunk) === index}
                    key={getCardKey(card, index)}
                  />
                ))}
              </div>
            ) : null}
            {isWorkFlowFinished ? (
              <Text className={styles.text}>{t('Modal.WizardEndTextKey')}</Text>
            ) : null}
          </FormFieldsContainer>
        </div>
      </ContentComponent>
    </FormBody>
  )
}

export default RequestWizardCardStageFormFields
